/* ==========================================================================
   Dwell — mobile nav drawer fix (bug fix only, no design change)
   --------------------------------------------------------------------------
   Two defects in the theme's MenuDrawer.onSummaryClick, for any submenu that
   is not the drawer's own open/close toggle:

     } else {
       trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector(`button`));
       setTimeout(() => detailsElement.classList.add(`menu-opening`));
     }

   1. It aims focus at the submenu's `back` button. This theme lays the drawer
      out as an accordion, so that button is display:none and cannot take
      focus — focus falls through to <body>. onFocusOut then sees focus
      outside the drawer and calls closeMenuDrawer, which strips `open` and
      `menu-opening` from EVERY <details> in the nav at once.

   2. It only ever ADDS `menu-opening`, never removes it. The method that
      removes it, closeSubmenu, is reachable only from that same hidden back
      button or the Escape key, so the class gets stuck on.

   A third problem, and the one that actually made the collections vanish,
   is not in the theme's code at all. On-device logging showed that when the
   rows were missing the DOM was entirely healthy — open=1, visibility visible,
   opacity 1, the list 382px tall with all 10 children, the first row 40px
   tall at y=162, well inside the viewport. Everything said the rows were
   there; they simply were not painted. That is an iOS Safari compositing
   bug: the drawer is a transformed, position:fixed layer, and reopening a
   <details> inside it does not always invalidate the stale tiles.

   This script leaves the theme's handler alone and corrects all three after
   the fact: it forces a repaint when a submenu opens, puts focus back on the
   row that was tapped, keeps `menu-opening` in step with the `open`
   attribute, and stops a focus loss that is going nowhere from closing the
   whole drawer.
   ========================================================================== */

(function () {
  'use strict';

  var ITEM_SUMMARY = 'summary.menu-drawer__menu-item';

  function syncOpeningClass(details) {
    if (details.hasAttribute('open')) {
      details.classList.add('menu-opening');
    } else {
      details.classList.remove('menu-opening');
    }
  }

  function focusWithoutScrolling(element) {
    try {
      element.focus({ preventScroll: true });
    } catch (err) {
      element.focus();
    }
  }

  /* iOS Safari can reopen a <details> without repainting the region it owns:
     every measurable property says the rows are laid out, visible and opaque,
     but the pixels on screen are the stale ones from before it closed. Taking
     the panel out of flow and putting it straight back, in the same frame,
     forces a fresh paint. No visible flash, because nothing yields in between. */
  function forceRepaint(submenu) {
    if (!submenu) return;
    var previous = submenu.style.display;
    submenu.style.display = 'none';
    void submenu.offsetHeight;
    submenu.style.display = previous || '';
  }

  function patch(drawer) {
    if (drawer.dataset.dwellNavFix === 'on') return;
    drawer.dataset.dwellNavFix = 'on';

    var mainToggle = drawer.querySelector('details');
    if (!mainToggle) return;

    /* A focus loss that isn't going anywhere outside the drawer must not be
       allowed to reach the theme's onFocusOut, which would close everything.
       A genuine tab-away (relatedTarget outside the drawer) still gets
       through, so that behaviour is preserved. */
    drawer.addEventListener(
      'focusout',
      function (event) {
        var movingTo = event.relatedTarget;
        if (!movingTo || drawer.contains(movingTo)) {
          event.stopPropagation();
        }
      },
      true
    );

    /* Runs after the theme's own click handler, which sits on the summary. */
    drawer.addEventListener('click', function (event) {
      var summary = event.target.closest && event.target.closest(ITEM_SUMMARY);
      if (!summary) return;

      var details = summary.parentNode;
      if (details === mainToggle || details.tagName !== 'DETAILS') return;

      focusWithoutScrolling(summary);
      requestAnimationFrame(function () {
        syncOpeningClass(details);
        if (details.hasAttribute('open')) {
          forceRepaint(details.querySelector(':scope > .menu-drawer__submenu'));
        }
      });
    });

    /* Catches every other route in and out — keyboard, Escape, back button. */
    Array.prototype.forEach.call(drawer.querySelectorAll(ITEM_SUMMARY), function (summary) {
      var details = summary.parentNode;
      if (details === mainToggle || details.tagName !== 'DETAILS') return;

      details.addEventListener('toggle', function () {
        syncOpeningClass(details);
        if (details.hasAttribute('open')) {
          forceRepaint(details.querySelector(':scope > .menu-drawer__submenu'));
        }
      });
      syncOpeningClass(details);
    });
  }

  function run() {
    Array.prototype.forEach.call(document.querySelectorAll('header-drawer'), patch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /* The theme editor re-renders the header section in place. */
  document.addEventListener('shopify:section:load', run);
})();
